import { type VfsEntry, VfsSession } from "./vfs.js";

export interface DownloadJob {
    /** Real asset path used for the browser download. */
    url: string;
    /** VFS path shown in wget/curl terminal output. */
    displayPath: string;
    filename: string;
    size: number;
}

export interface DownloadCommandResult {
    lines: string[];
    job?: DownloadJob;
    isError: boolean;
}

function formatTimestamp(): string {
    return new Date().toISOString().replace("T", " ").slice(0, 19);
}

function formatKiB(bytes: number): string {
    return `${(bytes / 1024).toFixed(2)}K`;
}

function resolveDownloadUrl(url: string): URL {
    if (/^https?:\/\//i.test(url)) {
        return new URL(url);
    }

    const base =
        typeof window !== "undefined" ? window.location.origin : "http://localhost";
    return new URL(url, base);
}

function isVfsLocalPath(path: string): boolean {
    return path.startsWith("/home/");
}

function findDownloadInDir(
    children: Record<string, VfsEntry>,
    matcher: (entry: VfsEntry, name: string) => DownloadJob | null,
): DownloadJob | null {
    for (const [name, entry] of Object.entries(children)) {
        const match = matcher(entry, name);
        if (match) return match;

        if (entry.type === "dir") {
            const nested = findDownloadInDir(entry.children, matcher);
            if (nested) return nested;
        }
    }

    return null;
}

function findDownloadJob(vfs: VfsSession, arg: string): DownloadJob | null {
    if (/^https?:\/\//i.test(arg)) {
        const home = vfs.lookup(vfs.home);
        if (home?.type !== "dir") return null;

        return findDownloadInDir(home.children, (entry, name) => {
            if (entry.type !== "file" || entry.downloadSrc !== arg) return null;
            return {
                url: entry.downloadSrc,
                displayPath: `${vfs.home}/${name}`,
                filename: name,
                size: entry.size,
            };
        });
    }

    const path = vfs.resolvePath(arg);
    if (!vfs.isPathAllowed(path)) return null;

    const entry = vfs.lookup(path);
    if (entry?.type !== "file" || !entry.downloadSrc) return null;

    const filename = path.split("/").pop() ?? arg;
    return { url: entry.downloadSrc, displayPath: path, filename, size: entry.size };
}

function wgetLines(job: DownloadJob): string[] {
    const stamp = formatTimestamp();

    if (isVfsLocalPath(job.displayPath)) {
        return [
            `--${stamp}--  ${job.displayPath}`,
            `Length: ${job.size} (${formatKiB(job.size)}) [application/pdf]`,
            `Saving to: ‘${job.filename}’`,
            "",
            `${job.filename}          100%[===================>] ${formatKiB(job.size)}  --.-KB/s    in 0s`,
            "",
            `${stamp} - ‘${job.filename}’ saved [${job.size}/${job.size}]`,
        ];
    }

    const url = resolveDownloadUrl(job.url);

    return [
        `--${stamp}--  ${url.href}`,
        `Resolving ${url.hostname} (${url.hostname})...`,
        `Connecting to ${url.hostname}|...|:443... connected.`,
        "HTTP request sent, awaiting response... 200 OK",
        `Length: ${job.size} (${formatKiB(job.size)}) [application/pdf]`,
        `Saving to: ‘${job.filename}’`,
        "",
        `${job.filename}          100%[===================>] ${formatKiB(job.size)}  --.-KB/s    in 0s`,
        "",
        `${stamp} - ‘${job.filename}’ saved [${job.size}/${job.size}]`,
    ];
}

function curlProgressLine(job: DownloadJob): string[] {
    return [
        "  % Total    % Received % Xferd  Average Speed   Time    Time     Time  Current",
        "                                 Dload  Upload   Total   Spent    Left  Speed",
        `100 ${job.size}  100 ${job.size}    0     0  9999k      0 --:--:-- --:--:-- --:--:-- 9999k`,
    ];
}

function runWget(args: string[], vfs: VfsSession): DownloadCommandResult {
    const targets = args.filter((arg) => !arg.startsWith("-"));
    if (targets.length === 0) {
        return { lines: ["wget: missing URL"], isError: true };
    }

    const target = targets[0]!;
    const job = findDownloadJob(vfs, target);
    if (!job) {
        if (/^https?:\/\//i.test(target)) {
            return {
                lines: [`wget: unable to resolve host address ‘${new URL(target).hostname}’`],
                isError: true,
            };
        }

        return { lines: [`wget: ${target}: No such file or directory`], isError: true };
    }

    return { lines: wgetLines(job), job, isError: false };
}

function runCurl(args: string[], vfs: VfsSession): DownloadCommandResult {
    let outputFile: string | null = null;
    let useRemoteName = false;
    const positional: string[] = [];

    for (let index = 0; index < args.length; index += 1) {
        const arg = args[index]!;
        if (arg === "-O" || arg === "--remote-name") {
            useRemoteName = true;
            continue;
        }

        if (arg === "-o" || arg === "--output") {
            outputFile = args[index + 1] ?? null;
            index += 1;
            continue;
        }

        if (arg.startsWith("-")) continue;
        positional.push(arg);
    }

    if (positional.length === 0) {
        return {
            lines: ["curl: try 'curl --help' or 'curl --manual' for more information"],
            isError: true,
        };
    }

    const target = positional[0]!;
    const job = findDownloadJob(vfs, target);
    if (!job) {
        if (/^https?:\/\//i.test(target)) {
            return { lines: [`curl: (6) Could not resolve host: ${new URL(target).hostname}`], isError: true };
        }

        return { lines: [`curl: ${target}: No such file or directory`], isError: true };
    }

    const filename = outputFile ?? (useRemoteName ? job.filename : null);
    if (!filename) {
        return {
            lines: [
                ...curlProgressLine(job),
                "(binary output suppressed — use curl -O or curl -o)",
            ],
            isError: false,
        };
    }

    return {
        lines: curlProgressLine({ ...job, filename }),
        job: { ...job, filename },
        isError: false,
    };
}

export function isDownloadCommand(input: string): boolean {
    const cmd = input.trim().split(/\s+/)[0]?.toLowerCase();
    return cmd === "wget" || cmd === "curl";
}

export function runDownloadCommand(input: string, vfs: VfsSession): DownloadCommandResult {
    const parts = input.trim().split(/\s+/);
    const cmd = parts[0]!.toLowerCase();
    const args = parts.slice(1);

    if (cmd === "wget") return runWget(args, vfs);
    return runCurl(args, vfs);
}
